import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BulkCreationTableRow } from '@/App/BulkTrade/BulkCreationTableRow.tsx';
import { useCheckBulkTradeWithTradeCapture } from '@/App/BulkTrade/BulkTradeContext.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { SgCheckbox } from '@/components/sg-design-system/components/input/SgCheckbox';
import { SgPicker } from '@/components/sg-design-system/components/input/SgPicker';
import type { SgPickerItem } from '@/components/sg-design-system/components/input/SgPicker/types.ts';
import { bulkTradeApi, useRequestBulkTradeRfsMutation } from '@/store/bulkTrade/api';
import { getRequestBulkTradeRfsMutationCacheKey } from '@/store/bulkTrade/api/requestBulkTradeRfs.ts';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import type { BulkLeg } from '@/store/bulkTrade/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

type BulkTableProps = {
  tabId: string;
};

export const BulkCreationTable = (props: BulkTableProps) => {
  const { tabId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const checkBulkTradeWithTradeCapture = useCheckBulkTradeWithTradeCapture();

  const [requestBulkTradeRFS] = useRequestBulkTradeRfsMutation({
    fixedCacheKey: getRequestBulkTradeRfsMutationCacheKey(tabId),
  });

  const {
    signalRConnectionId,
    bulkTradeId,
    clientId,
    currencyPair,
    currency1,
    currency2,
    negotiatedCurrencyIndex,
    allProducts,
    allSelectedProductIds,
    allExcludedProductIds,
    canExcludeProducts,
    canIncludeProducts,
    canRemoveSelection,
    productNameByProductId,
    activeLegs,
    activeLegCount,
    canPrice,
    nextLegId,
    isRfsPending,
    rfsId,
  } = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    const allProducts = bulkTrade?.step === 'building' ? Object.entries(bulkTrade.legs) : undefined;

    const allProductIds = Object.keys(bulkTrade?.legs ?? {});
    const allSelectedProductIds = bulkTrade?.allSelectedLegs ?? [];
    const allExcludedProductIds = bulkTrade?.allExcludedLegs ?? [];

    // there is at least one selected product that is not currently excluded,
    // and at least one unselected product that is not excluded,
    // so we can exclude it from the bulk
    const canExcludeProducts =
      allSelectedProductIds.some((productId) => !allExcludedProductIds.includes(productId)) &&
      allProductIds.some((productId) => {
        return !allSelectedProductIds.includes(productId) && !allExcludedProductIds.includes(productId);
      });

    // there is at least one excluded product that is currently selected, so we can include it back to the bulk
    const canIncludeProducts = allExcludedProductIds.some((productId) => allSelectedProductIds.includes(productId));

    // there is at least one unselected product, so we can remove it from the bulk
    const canRemoveSelection = allProductIds.some((productId) => !allSelectedProductIds.includes(productId));

    const activeLegsWithId = isDefined(bulkTrade?.legs)
      ? Object.entries(bulkTrade.legs).filter(([legId]) => {
          return !bulkTrade.allExcludedLegs.includes(legId);
        })
      : undefined;

    const activeLegCount = activeLegsWithId?.length ?? 0;

    const activeLegs = isDefined(activeLegsWithId) ? Object.fromEntries(activeLegsWithId) : undefined;

    return {
      signalRConnectionId: getSignalRConnectionId(state),
      bulkTradeId: bulkTrade?.id,
      clientId: selectTileClientId(state, tabId),
      currencyPair: bulkTrade?.currencyPair,
      currency1: bulkTrade?.currency1,
      currency2: bulkTrade?.currency2,
      negotiatedCurrencyIndex: bulkTrade?.negotiatedCurrencyIndex ?? 1,
      allProducts,
      allSelectedProductIds,
      allExcludedProductIds,
      canExcludeProducts,
      canIncludeProducts,
      canRemoveSelection,
      productNameByProductId: Object.entries(bulkTrade?.legs ?? []).reduce(
        (acc, [productId, leg]) => {
          acc[productId] = { type: leg.type };
          return acc;
        },
        {} as Record<string, { type: BulkLeg['type'] }>,
      ),
      legs: bulkTrade?.legs,
      activeLegs,
      activeLegCount,
      canPrice: bulkTrade?.isReadyToPrice ?? false,
      nextLegId: bulkTrade?.nextLegId,
      isRfsPending: bulkTrade?.rfsRequest?.status === 'pending',
      rfsId: bulkTrade?.rfsRequest?.rfsId,
      rfsError:
        isDefined(bulkTrade?.rfsRequest) && bulkTrade.rfsRequest.status === 'error'
          ? {
              code: bulkTrade.rfsRequest.code,
              message: bulkTrade.rfsRequest.message,
              rfsId: bulkTrade.rfsRequest?.rfsId,
            }
          : undefined,
    };
  }, deepEqual);

  const isAllProductsSelected =
    isDefined(allProducts) && allProducts.length > 0 && allSelectedProductIds.length === allProducts.length;

  const toggleAllProductsSelection = useCallback(() => {
    if (isNotDefined(allProducts)) {
      return;
    }

    dispatch(
      bulkTradeSlice.actions.toggleAllLegsSelection({
        tabId,
      }),
    );
  }, [tabId, allProducts]);

  const toggleProductSelection = useCallback(
    (productId: string) => {
      dispatch(
        bulkTradeSlice.actions.toggleLegSelection({
          tabId,
          legId: productId,
        }),
      );
    },
    [tabId],
  );

  const hasCurrencyPair = isDefined(currencyPair);

  const allProductTypes = [
    { value: 'Spot' as const, label: formatMessage({ id: 'bulkTrade.spot' }) },
    { value: 'Forward' as const, label: formatMessage({ id: 'bulkTrade.forward' }) },
    { value: 'Swap' as const, label: formatMessage({ id: 'bulkTrade.swap' }) },
  ];

  const handleAddProduct = ({ value }: SgPickerItem<'Spot' | 'Forward' | 'Swap'>) => {
    if (isNotDefined(allProducts) || isNotDefined(bulkTradeId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'add leg', value);

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      currencyPair,
      legChanges: {
        ...productNameByProductId,

        [String(nextLegId)]: {
          type: value,
        },
      },
    });
  };

  const handleDeleteSelectedProducts = () => {
    if (isNotDefined(bulkTradeId) || isNotDefined(currencyPair)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'delete selected legs');

    const allRetainedProducts =
      allProducts?.filter(([legId]) => {
        return !allSelectedProductIds.includes(legId);
      }) ?? [];

    const legChanges = allRetainedProducts.reduce(
      (acc, [legId, leg]) => {
        acc[legId] = { type: leg.type };

        return acc;
      },
      {} as Record<string, { type: BulkLeg['type'] }>,
    );

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      currencyPair,
      legChanges,
    });

    dispatch(bulkTradeSlice.actions.clearSelectedLegs({ tabId }));
  };

  const handleExcludeSelectedProducts = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'exclude selected legs');

    dispatch(
      bulkTradeSlice.actions.excludeSelectedLegs({
        tabId,
      }),
    );
  }, [tabId, sgwtWebAnalytics]);

  const handleIncludeSelectedProducts = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'include selected legs');

    dispatch(
      bulkTradeSlice.actions.includeSelectedLegs({
        tabId,
      }),
    );
  }, [tabId, sgwtWebAnalytics]);

  const handleResetProducts = () => {
    if (isNotDefined(bulkTradeId) || isNotDefined(currencyPair)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'reset');

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      currencyPair,
      legChanges: { [String(nextLegId)]: { type: 'Spot' } },
    });
  };

  const handlePriceRequest = () => {
    if (
      isNotDefined(signalRConnectionId) ||
      isNotDefined(currencyPair) ||
      isNotDefined(negotiatedCurrencyIndex) ||
      isNotDefined(activeLegs) ||
      isRfsPending
    ) {
      return;
    }
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'request price');

    requestBulkTradeRFS({
      tabId,
      signalRConnectionId,
      clientId,
      currencyPair,
      negotiatedCurrencyIndex,
      legs: activeLegs,
      rfsId: getNewRfsId(),
    });
  };

  const handleCancelRfs = () => {
    if (isNotDefined(rfsId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'cancel pending RSF');

    dispatch(
      bulkTradeApi.endpoints.cancelBulkTradeRfs.initiate({
        tabId,
        rfsId,
      }),
    );
  };

  return (
    <>
      <table
        className={clsx('bulk table table-sm table-bordered table-thead-bordered table-body-bordered w-100 mb-0', {
          'bg-lvl1': hasCurrencyPair,
          'bg-lvl3': !hasCurrencyPair,
        })}
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col style={{ width: '40px' }} />
          <col style={{ width: '170px' }} />
          <col style={{ width: '210px' }} />
          <col />
          <col />
          <col />
          <col />
        </colgroup>

        <thead>
          <tr className="align-middle">
            <th>
              <SgCheckbox
                dataE2e="select-all-checkbox"
                checked={isAllProductsSelected}
                onClick={toggleAllProductsSelection}
              />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.product" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.way" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.amount1" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.date1" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.amount2" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.date2" />
            </th>
          </tr>
        </thead>

        <tbody>
          {isDefined(bulkTradeId) && isDefined(allProducts) && isDefined(currency1) && isDefined(currency2) && (
            <>
              {allProducts.map(([productId, product]) => (
                <BulkCreationTableRow
                  key={productId}
                  tabId={tabId}
                  bulkTradeId={bulkTradeId}
                  productId={productId}
                  product={product}
                  productNameByProductId={productNameByProductId}
                  isSelected={allSelectedProductIds.includes(productId)}
                  toggleSelection={toggleProductSelection}
                  isExcluded={allExcludedProductIds.includes(productId)}
                  currency1={currency1}
                  currency2={currency2}
                  negotiatedCurrencyIndex={negotiatedCurrencyIndex}
                  isRfsPending={isRfsPending}
                />
              ))}

              <tr>
                <td />
                <td className="p-0">
                  <SgPicker<'Spot' | 'Forward' | 'Swap'>
                    itemSelection="single"
                    selectedValues={[]}
                    items={allProductTypes}
                    showIcon={false}
                    onChange={handleAddProduct}
                    placeholder={formatMessage({ id: 'bulkTrade.table.addProduct' })}
                    showClearButton={false}
                    size="md"
                    data-e2e="add-product-menu"
                  />
                </td>
                <td colSpan={5} />
              </tr>
            </>
          )}

          {!hasCurrencyPair && (
            <tr>
              <td className="text-center text-secondary" colSpan={7}>
                <FormattedMessage id="bulkTrade.pleaseChooseCurrencyPair" />
              </td>
            </tr>
          )}

          {allSelectedProductIds.length > 0 && (
            <tr>
              <td colSpan={7}>
                <HStack horizontalAlignment="end" verticalAlignment="center" gap="8px">
                  <SgButton
                    purpose="flat"
                    color="danger"
                    icon={'delete'}
                    iconPosition="start"
                    onClick={handleDeleteSelectedProducts}
                    disabled={!canRemoveSelection}
                    data-e2e="delete-selected-button"
                  >
                    <FormattedMessage id="bulkTrade.buttons.remove" />
                  </SgButton>

                  <SgButton
                    purpose="outline"
                    icon="block"
                    iconPosition="start"
                    onClick={handleExcludeSelectedProducts}
                    disabled={!canExcludeProducts}
                    data-e2e="exclude-selected-button"
                  >
                    <FormattedMessage id="bulkTrade.buttons.excludeFromBulk" />
                  </SgButton>

                  <SgButton
                    purpose="outline"
                    icon="check"
                    iconPosition="start"
                    onClick={handleIncludeSelectedProducts}
                    disabled={!canIncludeProducts}
                    data-e2e="include-selected-button"
                  >
                    <FormattedMessage id="bulkTrade.buttons.includeInBulk" />
                  </SgButton>
                </HStack>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {hasCurrencyPair && isDefined(allProducts) && allProducts.length > 0 && (
        <HStack horizontalAlignment="end" verticalAlignment="center" gap="8px">
          {!isRfsPending && (
            <SgButton
              purpose="outline"
              size="lg"
              icon="refresh"
              iconPosition="start"
              onClick={handleResetProducts}
              data-e2e="reset-button"
            >
              <FormattedMessage id="bulkTrade.buttons.reset" />
            </SgButton>
          )}

          {isRfsPending && (
            <SgButton purpose="outline" size="lg" onClick={handleCancelRfs} data-e2e="cancel-button">
              <FormattedMessage id="bulkTrade.buttons.cancel" />
            </SgButton>
          )}

          <SgButton
            purpose="primary"
            color="info"
            size="lg"
            onClick={handlePriceRequest}
            disabled={!canPrice}
            loading={isRfsPending}
            data-e2e="price-button"
          >
            <FormattedMessage id="bulkTrade.buttons.price" values={{ productCount: activeLegCount }} />
          </SgButton>
        </HStack>
      )}
    </>
  );
};
